import { useEffect, useState } from 'react';
import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { Fingerprint, KeyRound, LogIn } from 'lucide-react';
import type {
  ItemResponse,
  LoginResponse,
  PasskeyLoginOptionsResponse,
  PasskeyRegisterOptionsResponse,
  SessionResponse,
  TotpSetupResponse,
} from '@proerp/shared';
import { Button, FormField, Input, Spinner } from '@proerp/ui';
import { portalRequest } from '@/lib/apiClient';
import { guessDeviceLabel, storeToken } from '@/lib/auth';
import { nextStepAfterCredentials, STEP_META, type LoginStep } from '@/lib/loginFlow';
import { AuthShell, ErrorNote } from '@/components/AuthShell';
import { QrCanvas } from '@/components/QrCanvas';

/**
 * Anmeldung im Partnerportal (ADR 0085). Fachlich derselbe Ablauf wie intern
 * (Passwort + TOTP oder Passkey, ADR 0012/0034), nur gegen `/v1/portal/auth/*`
 * und ohne das ERP-Branding-Panel — hier soll klar sein, dass man im Portal
 * ist, nicht im ERP.
 */
export function LoginScreen({
  onSuccess,
  notice,
}: {
  onSuccess: () => void;
  /** Warum man (wieder) hier ist — z. B. nach dem erzwungenen Passwortwechsel. */
  notice?: string | null;
}) {
  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [setup, setSetup] = useState<TotpSetupResponse | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  /** Beide Faktoren vorhanden ⇒ Rückweg zur Auswahl anbieten. */
  const [hasChoice, setHasChoice] = useState(false);

  function fail(err: unknown, fallback: string): void {
    setError(err instanceof Error ? err.message : fallback);
    setIsPending(false);
  }

  async function submitCredentials(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setIsPending(true);
    try {
      const response = await portalRequest<ItemResponse<LoginResponse>>('/auth/login', {
        method: 'POST',
        anonymous: true,
        body: JSON.stringify({ email: email.trim(), password }),
      });
      setPendingToken(response.data.pendingToken);
      setHasChoice(response.data.status === 'SECOND_FACTOR_CHOICE');
      setStep(nextStepAfterCredentials(response.data.status));
      setIsPending(false);
    } catch (err) {
      fail(err, 'Anmeldung fehlgeschlagen.');
    }
  }

  async function startTotpSetup(): Promise<void> {
    setError(null);
    setIsPending(true);
    try {
      const response = await portalRequest<ItemResponse<TotpSetupResponse>>('/auth/totp/setup', {
        method: 'POST',
        anonymous: true,
        body: JSON.stringify({ pendingToken }),
      });
      setSetup(response.data);
      setStep('totp-setup');
      setIsPending(false);
    } catch (err) {
      fail(err, 'Einrichtung fehlgeschlagen.');
    }
  }

  async function submitCode(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setIsPending(true);
    try {
      const response = await portalRequest<ItemResponse<SessionResponse>>('/auth/totp/verify', {
        method: 'POST',
        anonymous: true,
        body: JSON.stringify({ pendingToken, code: code.trim() }),
      });
      storeToken(response.data.accessToken);
      onSuccess();
    } catch (err) {
      fail(err, 'Code ungültig.');
    }
  }

  /** Passkey-Ersteinrichtung — schließt den Login gleich mit ab (ADR 0034). */
  async function runPasskeySetup(): Promise<void> {
    setError(null);
    setIsPending(true);
    try {
      const options = await portalRequest<ItemResponse<PasskeyRegisterOptionsResponse>>(
        '/auth/passkey/setup-options',
        { method: 'POST', anonymous: true, body: JSON.stringify({ pendingToken }) },
      );
      const attestationResponse = await startRegistration({
        optionsJSON: options.data.options as PublicKeyCredentialCreationOptionsJSON,
      });
      const session = await portalRequest<ItemResponse<SessionResponse>>('/auth/passkey/setup', {
        method: 'POST',
        anonymous: true,
        body: JSON.stringify({
          pendingToken,
          challengeToken: options.data.challengeToken,
          attestationResponse,
          deviceLabel: guessDeviceLabel(),
        }),
      });
      storeToken(session.data.accessToken);
      onSuccess();
    } catch (err) {
      fail(err, 'Passkey-Einrichtung abgebrochen oder nicht verfügbar.');
    }
  }

  /** Passkey-Verifikation beim Login (Alternative zu TOTP). */
  async function runPasskeyVerify(): Promise<void> {
    setError(null);
    setIsPending(true);
    try {
      const options = await portalRequest<ItemResponse<PasskeyLoginOptionsResponse>>(
        '/auth/passkey/login-options',
        { method: 'POST', anonymous: true, body: JSON.stringify({ pendingToken }) },
      );
      const assertionResponse = await startAuthentication({
        optionsJSON: options.data.options as PublicKeyCredentialRequestOptionsJSON,
      });
      const session = await portalRequest<ItemResponse<SessionResponse>>('/auth/passkey/verify', {
        method: 'POST',
        anonymous: true,
        body: JSON.stringify({
          pendingToken,
          challengeToken: options.data.challengeToken,
          assertionResponse,
        }),
      });
      storeToken(session.data.accessToken);
      onSuccess();
    } catch (err) {
      fail(err, 'Passkey-Anmeldung abgebrochen oder nicht verfügbar.');
    }
  }

  // Die Passkey-Schritte lösen die Geräte-Abfrage aus, sobald der Schritt aktiv
  // wird — ein zusätzlicher Klick wäre nur eine Hürde. „Erneut versuchen"
  // bleibt für den Abbruch.
  useEffect(() => {
    if (step === 'passkey-verify') void runPasskeyVerify();
    if (step === 'passkey-setup') void runPasskeySetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const meta = STEP_META[step];

  return (
    <AuthShell title={meta.title} subtitle={meta.subtitle}>
      {notice && step === 'credentials' && (
        <p
          role="status"
          className="mb-4 rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground"
        >
          {notice}
        </p>
      )}
      {step === 'credentials' && (
        <form onSubmit={submitCredentials} className="flex flex-col gap-4">
          <FormField label="E-Mail" htmlFor="portalEmail" required>
            <Input
              id="portalEmail"
              type="email"
              autoComplete="username"
              placeholder="name@firma.de"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoFocus
              required
            />
          </FormField>
          <FormField label="Passwort" htmlFor="portalPassword" required>
            <Input
              id="portalPassword"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </FormField>
          <ErrorNote message={error} />
          {/* h-11 ≈ 44 px: die kleinste Fläche, die sich auf dem Telefon
              sicher treffen lässt. */}
          <Button type="submit" variant="primary" disabled={isPending} className="mt-1 h-11 w-full">
            {isPending ? <Spinner aria-hidden /> : <LogIn className="size-4" aria-hidden />}
            Anmelden
          </Button>
        </form>
      )}

      {(step === 'setup-choice' || step === 'second-factor-choice') && (
        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="outline"
            className="h-auto justify-start gap-3 py-3"
            onClick={() => (step === 'setup-choice' ? void startTotpSetup() : setStep('totp-verify'))}
            disabled={isPending}
          >
            <KeyRound className="size-5 shrink-0" aria-hidden />
            <span className="text-left">
              <span className="block font-medium">Authenticator-App</span>
              <span className="block text-xs text-muted-foreground">
                6-stelliger Code aus einer App wie Google Authenticator oder 1Password
              </span>
            </span>
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-auto justify-start gap-3 py-3"
            onClick={() => setStep(step === 'setup-choice' ? 'passkey-setup' : 'passkey-verify')}
            disabled={isPending}
          >
            <Fingerprint className="size-5 shrink-0" aria-hidden />
            <span className="text-left">
              <span className="block font-medium">Passkey</span>
              <span className="block text-xs text-muted-foreground">
                Face ID, Touch ID oder Geräte-PIN — ohne Code-Eingabe
              </span>
            </span>
          </Button>
          <ErrorNote message={error} />
        </div>
      )}

      {step === 'totp-setup' && setup && (
        <form onSubmit={submitCode} className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-3">
            <QrCanvas value={setup.otpauthUrl} />
            <p className="text-center text-xs text-muted-foreground">
              Lässt sich der Code nicht scannen, gib dieses Geheimnis manuell ein:
              <br />
              <code className="mt-1 inline-block font-mono text-[0.7rem] tracking-wider">{setup.secret}</code>
            </p>
          </div>
          <FormField label="Code aus der App" htmlFor="portalTotpSetupCode" required>
            <Input
              id="portalTotpSetupCode"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoFocus
              required
            />
          </FormField>
          <ErrorNote message={error} />
          <Button type="submit" variant="primary" disabled={isPending} className="h-11 w-full">
            {isPending && <Spinner aria-hidden />}
            Einrichtung abschließen
          </Button>
        </form>
      )}

      {step === 'totp-verify' && (
        <form onSubmit={submitCode} className="flex flex-col gap-4">
          <FormField label="Code aus der App" htmlFor="portalTotpCode" required>
            <Input
              id="portalTotpCode"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoFocus
              required
            />
          </FormField>
          <ErrorNote message={error} />
          <Button type="submit" variant="primary" disabled={isPending} className="h-11 w-full">
            {isPending && <Spinner aria-hidden />}
            Bestätigen
          </Button>
          {hasChoice && (
            <Button type="button" variant="ghost" onClick={() => setStep('second-factor-choice')}>
              Anderen Faktor wählen
            </Button>
          )}
        </form>
      )}

      {(step === 'passkey-setup' || step === 'passkey-verify') && (
        <div className="flex flex-col gap-4">
          {isPending && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner aria-hidden />
              Warte auf die Bestätigung deines Geräts …
            </p>
          )}
          <ErrorNote message={error} />
          {!isPending && (
            <Button
              type="button"
              variant="primary"
              className="h-11 w-full"
              onClick={() => (step === 'passkey-setup' ? void runPasskeySetup() : void runPasskeyVerify())}
            >
              <Fingerprint className="size-4" aria-hidden />
              Erneut versuchen
            </Button>
          )}
          {(hasChoice || step === 'passkey-setup') && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setStep(step === 'passkey-setup' ? 'setup-choice' : 'second-factor-choice')}
            >
              Anderen Faktor wählen
            </Button>
          )}
        </div>
      )}
    </AuthShell>
  );
}

